<script setup>
  import { ref, computed, useTemplateRef, onMounted, onUnmounted, defineAsyncComponent } from 'vue';
  import { useMapDrawingStore } from '@/stores/MapDrawingStore';
  import { useMapDrawingManager } from "@/composables/useMapDrawingManager";

  import MapDrawingShape from '@/components/MapDrawingShape.vue';
  import MapDrawingShapeSelection from '@/components/MapDrawingShapeSelection.vue';
  import MapDrawingShapeDistanceMarkers from '@/components/MapDrawingShapeDistanceMarkers.vue';
  import MapDrawingShapeControlToolbar from '@/components/MapDrawingShapeControlToolbar.vue';

  const MapDrawingShapeControlSegments = defineAsyncComponent(() => import('@/components/MapDrawingShapeControlSegments.vue'))
  const MapDrawingShapeControlHandles = defineAsyncComponent(() => import('@/components/MapDrawingShapeControlHandles.vue'))
  const MapDrawingShapeDistanceInputController = defineAsyncComponent(() => import('@/components/MapDrawingShapeDistanceInputController.vue'))

  const mapDrawingStore = useMapDrawingStore();

  const drawingManagerDiv = useTemplateRef('drawing-manager-div');

  const drawingManager = useMapDrawingManager();
  const map = window.$JWX.map;

  let timeout;

  const mapListeners = ref([]);
  const drawingManagerLoaded = ref(false);

  const zooming = ref(false);
  const isControlClickInteraction = ref(false);
  const controlPayload = ref(null);
  const controlMoveInProgress = ref(false);

  const isEditingMode = computed(() => mapDrawingStore.mode === 'editing' && mapDrawingStore.activeShape);
  const useControlSegments = computed(() => isEditingMode.value && (mapDrawingStore.activeShape.type === 'polyline' || mapDrawingStore.activeShape.type === 'polygon'));
  const showDistanceInputMarker = computed(() => isEditingMode.value && mapDrawingStore.activeShape.type === 'circle' && !zooming.value);

  const getCircleRadius = (latLon1, latLon2) => {
    const radius = drawingManager.getDistanceBetweenLatLons(latLon1, latLon2);

    return Number(radius.toFixed(1));
  }

  const setCircleRadius = () => {
    if (mapDrawingStore.activeShape.type !== 'circle') return;

    const latLon1 = mapDrawingStore.activeShape.latLons[0];
    const latLon2 = mapDrawingStore.activeShape.latLons[1];

    const radius = getCircleRadius(latLon1, latLon2);
    mapDrawingStore.setCircleRadius(radius);
  }

  const handleMapClick = (e) => {
    if (!e) return;

    if (
      mapDrawingStore.mode !== 'editing' || 
      mapDrawingStore.shapes.length === 0 || 
      mapDrawingStore.activeShape.type === 'polygon' && 
      mapDrawingStore.activeShape.closed &&
      !isControlClickInteraction.value ||
      mapDrawingStore.activeShape.type === 'circle' && 
      mapDrawingStore.activeShape.latLons.length === 2
    ) {
      isControlClickInteraction.value = false;
      controlPayload.value = null;
      return;
    }

    const point = drawingManager.getPointFromLatLon(e.latLng);
    const id = Date.now();
    const pointWithId = { id, ...point };
    const latLonWithId = { id, lat: e.latLng.lat(), lng: e.latLng.lng() };
    
    if (isControlClickInteraction.value) {
      const { type, index } = controlPayload.value;
      map.setOptions({gestureHandling: 'none'});

      switch(type) {
        case 'ignore':
        case 'distance-control':
        case 'handle':
        case 'shape-complete':
          break;
        case 'segment':
          mapDrawingStore.activeShape.latLons.splice(index, 0, latLonWithId);
          mapDrawingStore.activeShape.points.splice(index, 0, pointWithId);
          break;
        case 'delete':
          mapDrawingStore.activeShape.latLons.splice(index, 1);
          mapDrawingStore.activeShape.points.splice(index, 1);
          break;
      }

    } else {
      mapDrawingStore.activeShape.latLons.push(latLonWithId);
      mapDrawingStore.activeShape.points.push(pointWithId);

      if (mapDrawingStore.activeShape.type === 'circle' && mapDrawingStore.activeShape.latLons.length === 2) {
        setCircleRadius();
      }      
    }

    isControlClickInteraction.value = false;
    controlPayload.value = null;

    map.setOptions({gestureHandling: 'auto'});
  };

  const handleMapDblClick = (e) => {
    if (!e) return;

    if (isControlClickInteraction.value) {
      return;
    }

    // Reimplement the double click to zoom functionality
    const map = drawingManager.map();
    map.setCenter(e.latLng);
    map.setZoom(map.getZoom() + 1);
  }

  const handleMapDragend = (e) => {
    // Reset gesture handling to 'auto' incase a ui event turned it off
    map.setOptions({gestureHandling: 'auto'});
  }

  const rebuildShapes = () => {
    mapDrawingStore.shapes.forEach(shape => {
      shape.latLons = shape.latLons.map((latLon, i) => {
        return { id: `${Date.now()}-${i}`, lat: latLon.lat, lng: latLon.lng }
      })
    });

    convertShapeLatLonsToPoints();
  }  

  const handleMapIdle = () => {
    if (zooming.value) {
      
      zooming.value = false;
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(() => {
      drawingManager.rebuild({div: drawingManagerDiv.value, map, callback: rebuildShapes});
    }, 100);
  }

  const handleZoomChange = () => {
    zooming.value = true;
  }

  const handleKeyPress = (e) => {
    const key = e.key; // Get the key that was pressed

    // Check for specific keys
    if (key === 'Escape') {
      deactivateDrawingMode();
    }
  }  

  const handleControlClick = (e) => {   
    isControlClickInteraction.value = true;
    controlPayload.value = e;

  }
  
  const handleDistanceControlFocus = (e) => {
    isControlClickInteraction.value = true;
    controlPayload.value = e;
  }

  const handleDistanceControlChange = (e) => {
    const { value: distance } = e;
    
    const heading = mapDrawingStore.activeShape.latLons.length === 2 ? drawingManager.getHeadingBetweenLatLons(mapDrawingStore.activeShape.latLons[0], mapDrawingStore.activeShape.latLons[1]) : 0;
    const latLon1 = mapDrawingStore.activeShape.latLons[0];

    const latLon2 = drawingManager.getLatLonAtDistance({ latLon: latLon1, distance, heading });
    const point2 = drawingManager.getPointFromLatLon(latLon2);

    const id = Date.now();
    const latLon2WithId = { id, lat: latLon2.lat(), lng: latLon2.lng() };
    const point2WithId = { id, ...point2 };

    mapDrawingStore.activeShape.latLons = [mapDrawingStore.activeShape.latLons[0], latLon2WithId];
    mapDrawingStore.activeShape.points = [mapDrawingStore.activeShape.points[0], point2WithId];

    if (mapDrawingStore.activeShape.type === 'circle') {
      setCircleRadius();
    }    
  }

  const handleControlMove = (e) => {
    map.setOptions({gestureHandling: 'none'});
    const { points, index, startingPoint } = e;

    controlMoveInProgress.value = true;

    if (mapDrawingStore.activeShape.type === 'circle' && startingPoint) {
      mapDrawingStore.activeShape.points = points.map((point, i) => {
        return {
          id: `${Date.now()}-${i}`,
          ...point,
        }
      });
    } else {
      mapDrawingStore.activeShape.points[index] = {
        id: `${Date.now()}-0`,
        ...points[0],
      };
    }

    convertShapePointsToLatLons();

    if (mapDrawingStore.activeShape.type === 'circle') {
      setCircleRadius();
    }    
  }

  const handleControlCancel = (e) => {
    map.setOptions({gestureHandling: 'auto'});

    convertShapePointsToLatLons();
    controlMoveInProgress.value = false;

    if (mapDrawingStore.activeShape.type === 'circle') {
      setCircleRadius();
    }
  }

  const handleStartingPointClick = (e) => {
    if (mapDrawingStore.activeShape.type === 'polygon') {
      mapDrawingStore.activeShape.closed = true;
    }
  }

  const handleShapeSelectionClick = (e) => {
    const { id } = e;

    mapDrawingStore.setActiveShapeId(id);
    mapDrawingStore.setDrawingMode('editing');
    mapDrawingStore.setSelectedDrawingTool(mapDrawingStore.activeShape.type);

    isControlClickInteraction.value = true;
    controlPayload.value = e;
  }

  const handleShapeDeleteClick = (e) => {
    mapDrawingStore.deleteShape();
    mapDrawingStore.deactivateDrawingMode();
  }

  const convertShapeLatLonsToPoints = () => {
    mapDrawingStore.shapes.forEach((shape) => {
      const points = drawingManager.getPointsFromLatLons(shape.latLons);

      shape.points = [];
      shape.points = points.map((point, i) => ({ id: `${Date.now()}-${i}`, ...point }));
    });
  }

  const convertShapePointsToLatLons = () => {
    mapDrawingStore.shapes.forEach((shape) => {
      const latLons = drawingManager.getLatLonsFromPoints(shape.points);

      shape.latLons = [];
      shape.latLons = latLons.map((latLon, i) => ({ id: `${Date.now()}-${i}`, lat: latLon.lat(), lng: latLon.lng() }));
    });
  }

  const handleRangeSliderDrag = (e) => {
    const { type } = e;

    if (type === 'ignore') {
      map.setOptions({gestureHandling: 'none'});
    }
  }

  const deactivateDrawingMode = () => {
    isControlClickInteraction.value = false;
    controlPayload.value = null;
    mapDrawingStore.deactivateDrawingMode();
  }

  const initCallback = () => {
    convertShapeLatLonsToPoints();
    drawingManagerLoaded.value = true
  }

  // These are workarounds to ensure that the map events are triggered 
  // after events attached to the drawing ui elements
  const delayedMapClick = (e) => {    
    setTimeout(() => {
      handleMapClick(e);
    }, 0);
  }

  const delayedMapDblClick = (e, disableDoubleclick = true) => {    
    map.setOptions({disableDoubleClickZoom: true });

    if (disableDoubleclick) {
      setTimeout(() => {
        handleMapDblClick(e);
      }, 0);
    }
  }
  
  onMounted(() => {
    drawingManager.init({div: drawingManagerDiv.value, map, callback: initCallback});

    mapListeners.value.push(map.addListener('click', delayedMapClick));
    mapListeners.value.push(map.addListener('dblclick', delayedMapDblClick));
    mapListeners.value.push(map.addListener('dragend', handleMapDragend));
    mapListeners.value.push(map.addListener('drawing-passing-through-event', handleMapClick));
    mapListeners.value.push(map.addListener('zoom_changed', handleZoomChange));
    mapListeners.value.push(map.addListener('idle', handleMapIdle));

    document.addEventListener('keydown', handleKeyPress);
  })
  
  onUnmounted(() => {
    mapListeners.value.forEach((listener) => {
      google.maps.event.removeListener(listener);
    });
    
    mapListeners.value = [];

    isControlClickInteraction.value = false;
    controlPayload.value = null;
    mapDrawingStore.deactivateDrawingMode();
    drawingManager.remove();    
  });
</script>

<template>
  <div v-show="!zooming" ref="drawing-manager-div" class="jwx-map-drawing__manager">

    <svg v-if="drawingManagerLoaded" xmlns="http://www.w3.org/2000/svg" class="jwx-map-drawing__canvas" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
      <MapDrawingShape 
        v-for="shape in mapDrawingStore.shapes"
        :shape="shape"
        :drawingManager="drawingManager"
        :key="shape.id"
      />
      <MapDrawingShapeSelection
        v-for="shape in mapDrawingStore.shapes"
        @shape-selection:click="handleShapeSelectionClick"
        :shape="shape"
        :drawingManager="drawingManager"
        :key="shape.id"
      />
    </svg>
    <div v-if="drawingManagerLoaded" :data-mode="isEditingMode ? 'editing' : 'viewing'" class="jwx-map-drawing__controller">
      <MapDrawingShapeDistanceMarkers
        v-for="shape in mapDrawingStore.shapes"
        :drawingManager="drawingManager"
        :shape="shape"
        :showDistanceMarkers="mapDrawingStore.showDistanceMarkers"
        :showHeadingsMarkers="mapDrawingStore.showHeadingMarkers"
        :activeShape="mapDrawingStore.activeShape?.id === shape.id"
        :key="shape.id"
      />  
      <MapDrawingShapeControlSegments
        v-if="useControlSegments"
        @segment:click="handleControlClick"
        :drawingManager="drawingManager"
        :points="mapDrawingStore.activeShape.points"
        :shapeClosed="mapDrawingStore.activeShape.closed"
      />
      <MapDrawingShapeControlHandles
        v-for="shape in mapDrawingStore.shapes"
        @handle:click="handleControlClick"
        @handle:pointermove="handleControlMove"
        @handle:pointercancel="handleControlCancel"
        @startingPoint:click="handleStartingPointClick"
        :drawingManager="drawingManager"
        :shape="shape"
        :activeShape="mapDrawingStore.activeShape?.id === shape.id"
        :key="shape.id"
      />
      <MapDrawingShapeDistanceInputController
        v-if="showDistanceInputMarker"
        @distance-control:change="handleDistanceControlChange"
        @distance-control:focus="handleDistanceControlFocus"
        :drawingManager="drawingManager"
        :shape="mapDrawingStore.activeShape"
        :latLons="mapDrawingStore.activeShape.latLons"
        :distance="mapDrawingStore.activeShape.settings.radius"
      />
      <MapDrawingShapeControlToolbar
        v-for="shape in mapDrawingStore.shapes"
        @control-toolbar:click="handleControlClick"
        @control-toolbar:dblclick="handleControlClick"
        @shape-selection:click="handleShapeSelectionClick"
        @shape-complete:click="deactivateDrawingMode"
        @shape-delete:click="handleShapeDeleteClick"
        @range-slider:drag="handleRangeSliderDrag"
        :drawingManager="drawingManager"
        :shape="shape"
        :activeShape="mapDrawingStore.activeShape?.id === shape.id"
        :key="shape.id"
      />      
    </div>
  </div>
</template>